import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import { Box, CircularProgress, Typography, Button } from "@mui/material";
import { BaseFormPage } from "../../components/common/BaseForm";
import { useFormCompletion, useFormSubmission } from "../../hooks";
import { UserFormContent } from "./components/UserFormLayout";
import { 
  userFormValidationSchema,
  getInitialUserFormValues 
} from "./validation/userFormValidation";
import { UserFormValues } from "./types/userFormTypes";
import { 
  useGetUserByIdQuery,
  useUpdateUserMutation
} from "../../api/usersApi";
import { useAppSelector } from "../../app/hooks";
import { UserRoles } from "../../constants/roles";

const UserEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const userId = id ? parseInt(id, 10) : 0;
  const currentUserRole = useAppSelector((state) => state.auth.user?.role);
  const isAdminOrSuperAdmin =
    currentUserRole === UserRoles.ADMIN || currentUserRole === UserRoles.SUPER_ADMIN;
  const entityLabel = isAdminOrSuperAdmin ? "User" : "Driver";
  
  const [updateUser] = useUpdateUserMutation();

  const {
    data: user,
    isLoading: isLoadingUser,
    error: userError,
    refetch: refetchUser,
  } = useGetUserByIdQuery(userId, { skip: !userId || isNaN(userId) });

  // Initialize form values from user data
  const getInitialValues = (): UserFormValues => {
    if (!user) {
      return getInitialUserFormValues();
    }


    return {
      // User Information
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      email: user.email || "",
      role: user.role || "Dispatcher",
      
      // Employee Information
      isEmployee: user.isEmployee || false,
      employmentStartDate: user.employmentStartDate || null,
      employmentEndDate: user.employmentEndDate || null,
      dateOfBirth: user.dateOfBirth || null,
      familyHoliday: user.familyHoliday || null,
      worksWeekends: user.worksWeekends ?? false,
      phoneNumber: user.phoneNumber || "",
      
      // Address Information
      address: {
        street: user.street || "",
        city: user.city || "",
        state: user.state || "",
        zipCode: user.zipCode || "",
        country: user.country || "",
      },
      
      // Emergency Contact
      emergencyContact: {
        name: user.emergencyContactName || "",
        phone: user.emergencyContactPhone || "",
      },
      
      // Driver Information
      driverInfo: {
        companyName: user.driverInfo?.companyName || "",
        ein: user.driverInfo?.ein || "",
        ssn: user.driverInfo?.ssn || "",
      },
    };
  };

  const {
    isSubmitting, 
    submitError, 
    successMessage, 
    showValidationErrors, 
    handleSubmit 
  } = useFormSubmission({
    onSuccess: () => {
      setTimeout(() => {
        navigate("/users");
      }, 1500);
    },
    successMessage: `${entityLabel} updated successfully!`,
  });

  // Create formik instance directly, but only when user data is available
  const formik = useFormik<UserFormValues>({
    initialValues: user ? getInitialValues() : getInitialUserFormValues(),
    validationSchema: userFormValidationSchema,
    enableReinitialize: true, // Allow reinitialization when user data changes
    onSubmit: async (values) => {
      // If invalid, mark fields as touched to show errors and block submit
      const errors = await formik.validateForm();
      if (Object.keys(errors).length > 0) {
        formik.setTouched(
          Object.keys(errors).reduce((acc: any, key) => {
            acc[key] = true;
            return acc;
          }, {}),
          true
        );
        return;
      }

      await handleSubmit(async () => {
        const updateUserDto: any = {
          email: values.email,
          firstName: values.firstName,
          lastName: values.lastName,
          role: values.role,
          isEmployee: values.isEmployee,
          isActive: true, // Default value for employees
          employmentStartDate: values.employmentStartDate || undefined,
          employmentEndDate: values.employmentEndDate === null ? null : (values.employmentEndDate || undefined),
          dateOfBirth: values.dateOfBirth || undefined,
          familyHoliday: values.familyHoliday || undefined,
          worksWeekends: values.worksWeekends,
          // Send flat properties instead of nested objects
          street: values.isEmployee ? (values.address.street || undefined) : undefined,
          city: values.isEmployee ? (values.address.city || undefined) : undefined,
          state: values.isEmployee ? values.address.state : undefined,
          zipCode: values.isEmployee ? (values.address.zipCode || undefined) : undefined,
          country: values.isEmployee ? (values.address.country || undefined) : undefined,
          phoneNumber: values.isEmployee ? (values.phoneNumber || undefined) : undefined,
          emergencyContactName: values.isEmployee ? (values.emergencyContact.name || undefined) : undefined,
          emergencyContactPhone: values.isEmployee ? values.emergencyContact.phone : undefined,
          // For Dispatcher role, send null for driverInfo (backend validation expects this)
          // For TruckDriver role, send the driver info
          driverInfo: (values.isEmployee && values.role === "TruckDriver") ? {
            companyName: values.driverInfo.companyName || undefined,
            ein: values.driverInfo.ein || undefined,
            ssn: values.driverInfo.ssn || undefined,
          } : null,
        };

        const result = await updateUser({ id: userId, user: updateUserDto }).unwrap();
        
        // Force refetch the user data to get the latest values
        await refetchUser();
        
        return result;
      });
    },
  });

  const completionPercentage = useFormCompletion(formik.values);

  const handleBack = () => {
    navigate("/users");
  };

  const getErrorMessage = (error: any): string => {
    if (error?.status === 404) {
      return `${entityLabel} not found`;
    }
    if (error?.data?.message) {
      return error.data.message;
    }
    return `An error occurred while loading the ${entityLabel.toLowerCase()}`;
  };

  // Loading state
  if (isLoadingUser) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (userError || !user) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px">
        <Typography variant="h6" color="error" gutterBottom>
          {userError ? getErrorMessage(userError) : `${entityLabel} not found`}
        </Typography>
        <Button variant="contained" onClick={handleBack}>
          Back to {isAdminOrSuperAdmin ? "Users" : "Drivers"}
        </Button>
      </Box>
    );
  }

  return (
    <BaseFormPage
      title={`Edit ${entityLabel}`}
      subtitle={user ? `${user.firstName} ${user.lastName}` : undefined}
      onBack={handleBack}
      formik={formik}
      isSubmitting={isSubmitting}
      successMessage={successMessage}
      errorMessage={submitError}
      completionPercentage={completionPercentage}
      showValidationErrors={showValidationErrors}
      submitButtonText="Save Changes"
    >
      <UserFormContent
        mode="edit"
        onSubmit={() => {}} // BaseFormPage handles submission
        isLoading={isSubmitting}
        userId={userId}
        emailConfirmed={user?.emailConfirmed || false}
        originalEmail={user?.email || ""}
      />
    </BaseFormPage>
  );
};

export default UserEditPage;